"use client"

import { useState, useEffect, useRef } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { Menu, X } from "lucide-react"
import { useTranslations } from "next-intl"
import {
  getBlogArticleSlug,
  withBlogRegisterMagnetUtm,
  withOwnedBlogUtm,
} from "@/features/blog/lib/owned-utm"
import { Logo } from "@/shared/components/logo"
import { useCurrentLocale } from "@/shared/hooks/use-current-locale"
import { blogIndexPath, localizePath, stripLocaleFromPath } from "@/shared/lib/i18n/config"
import { subscribeToMediaQuery } from "@/shared/lib/media-query"
import { cn } from "@/shared/lib/utils"

const navLinks = [
  { href: "/explore", labelKey: "explore" },
  { href: "/create", labelKey: "create" },
  { href: "/pricing", labelKey: "pricing" },
] as const

export function BlogHeader() {
  const t = useTranslations("blog.header")
  const pathname = usePathname()
  const locale = useCurrentLocale()
  const normalizedPathname = stripLocaleFromPath(pathname)
  const isArticlePage = normalizedPathname.startsWith("/blog/") && normalizedPathname !== "/blog"
  const articleSlug = getBlogArticleSlug(normalizedPathname)

  const [scrolled, setScrolled] = useState(false)
  const [headerVisible, setHeaderVisible] = useState(true)
  const [isMobileViewport, setIsMobileViewport] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const lastScrollY = useRef(0)

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)")
    const handleViewportChange = () => setIsMobileViewport(mediaQuery.matches)

    handleViewportChange()
    return subscribeToMediaQuery(mediaQuery, handleViewportChange)
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      setScrolled(currentScrollY > 10)

      if (!isArticlePage || !isMobileViewport) {
        setHeaderVisible(true)
        lastScrollY.current = currentScrollY
        return
      }

      if (mobileOpen || currentScrollY < 80) {
        setHeaderVisible(true)
        lastScrollY.current = currentScrollY
        return
      }

      const delta = currentScrollY - lastScrollY.current
      if (Math.abs(delta) > 6) {
        setHeaderVisible(delta < 0)
      }
      lastScrollY.current = currentScrollY
    }

    handleScroll()
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [isArticlePage, isMobileViewport, mobileOpen])

  function localizeCtaPath(href: string): string {
    return localizePath(withOwnedBlogUtm(href, articleSlug), locale)
  }

  function localizeRegisterMagnetPath(): string {
    if (!articleSlug) {
      return localizePath("/register", locale)
    }
    return localizePath(withBlogRegisterMagnetUtm("/register", articleSlug, "header_cta"), locale)
  }

  return (
    <header
      className={cn(
        "top-0 z-50 border-b transition-all duration-300 ease-out",
        isArticlePage && isMobileViewport ? "sticky" : isArticlePage ? "relative" : "sticky",
        isArticlePage ? "border-[#3a57fc] bg-[#3a57fc]" : "",
        isArticlePage && isMobileViewport && (headerVisible ? "translate-y-0" : "-translate-y-full"),
        !isArticlePage && scrolled
          ? "border-gray-200 bg-white/95 shadow-sm backdrop-blur-md"
          : !isArticlePage
            ? "border-gray-100 bg-white"
            : ""
      )}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo links to home; "blog" fades out on hover */}
        <Link href={localizePath("/", locale)} className="group flex items-center gap-2">
          <Logo size="sm" tone={isArticlePage ? "inverse" : "onLight"} />
          <span
            className={cn(
              "text-lg font-light transition-opacity duration-200 group-hover:opacity-0",
              isArticlePage ? "text-white/55" : "text-gray-400"
            )}
          >
            {t("blogBrand")}
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-8 md:flex">
          <Link
            href={blogIndexPath(locale)}
            className={cn(
              "text-[14px] font-medium transition-colors",
              isArticlePage ? "text-white/75 hover:text-white" : "text-gray-600 hover:text-gray-900"
            )}
          >
            {t("blog")}
          </Link>
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={localizeCtaPath(link.href)}
              className={cn(
                "text-[14px] font-medium transition-colors",
                isArticlePage ? "text-white/75 hover:text-white" : "text-gray-600 hover:text-gray-900"
              )}
            >
              {t(link.labelKey)}
            </Link>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden items-center gap-3 md:flex">
          <Link
            href={localizeRegisterMagnetPath()}
            className={cn(
              "inline-flex items-center rounded-lg px-5 py-2 text-[14px] font-semibold transition-all duration-200",
              isArticlePage
                ? "bg-white text-[#111] hover:bg-white/90"
                : "bg-[#1d7df2] text-white shadow-md shadow-blue-500/20 hover:bg-[#1563c4] hover:shadow-blue-500/30"
            )}
          >
            {t("registerMagnetCta")}
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          className={cn(isArticlePage ? "text-white" : "text-gray-600", "md:hidden")}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? t("closeMenu") : t("openMenu")}
        >
          {mobileOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </button>
      </div>

      {/* Mobile nav dropdown */}
      {mobileOpen && (
        <div className="border-t border-gray-100 bg-white md:hidden">
          <nav className="flex flex-col px-4 py-4">
            <Link
              href={blogIndexPath(locale)}
              className="rounded-lg px-3 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              onClick={() => setMobileOpen(false)}
            >
              {t("blogHome")}
            </Link>
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={localizeCtaPath(link.href)}
                className="rounded-lg px-3 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                onClick={() => setMobileOpen(false)}
              >
                {t(link.labelKey)}
              </Link>
            ))}
            <div className="mt-3 px-3">
              <Link
                href={localizeCtaPath("/register")}
                className="block w-full rounded-lg bg-[#1d7df2] px-4 py-2.5 text-center text-sm font-semibold text-white transition-colors hover:bg-[#1563c4]"
                onClick={() => setMobileOpen(false)}
              >
                {t("registerMagnetCta")}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
